import type { SheetAnalysis } from './types'

const nullableString = { type: ['string', 'null'] }
const nullableSex = { anyOf: [{ type: 'string', enum: ['M', 'H', 'DUDOSO'] }, { type: 'null' }] }

export const sheetAnalysisSchema = {
  type: 'object',
  properties: {
    anio: { type: ['integer', 'null'] },
    hoja: { type: ['integer', 'null'] },
    padre: {
      type: 'object',
      properties: {
        nombre: nullableString,
        anilla: nullableString,
        plumaje: nullableString
      },
      required: ['nombre', 'anilla', 'plumaje'],
      additionalProperties: false
    },
    madre: {
      type: 'object',
      properties: {
        nombre: nullableString,
        anilla: nullableString,
        plumaje: nullableString
      },
      required: ['nombre', 'anilla', 'plumaje'],
      additionalProperties: false
    },
    pichones: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          anilla: nullableString,
          plumaje: nullableString,
          sexo: { ...nullableSex, description: 'Copia únicamente la marca visible en la celda: M, H, DUDOSO si es ilegible, o null si está vacía. No interpretes su significado.' },
          observaciones: { ...nullableString, description: 'Texto realmente escrito en observaciones. No confundas el trazo de tachado con una palabra.' },
          tachado: { type: 'boolean', description: 'true si una línea o trazo cruza y anula esta fila del pichón; false en caso contrario.' }
        },
        required: ['anilla', 'plumaje', 'sexo', 'observaciones', 'tachado'],
        additionalProperties: false
      }
    },
    campos_dudosos: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          campo: { type: 'string' },
          motivo: { type: 'string' }
        },
        required: ['campo', 'motivo'],
        additionalProperties: false
      }
    }
  },
  required: ['anio', 'hoja', 'padre', 'madre', 'pichones', 'campos_dudosos'],
  additionalProperties: false
} as const

export const extractionInstructions = `
Extrae datos de una hoja manuscrita de cría de palomos. Devuelve únicamente el JSON solicitado.

Reglas:
- No inventes datos ni completes nombres por contexto.
- Si algo no es legible, usa null y añade el campo a campos_dudosos con un motivo breve.
- La anilla es un identificador crítico. No cambies letras dudosas.
- Normaliza únicamente anillas claramente legibles: AK500153 o AK 50 01 53 se convierten en AK-500153.
- Conserva literalmente vocabulario de plumajes, incluyendo Gabino, Gavino, Toscado, Moscado, Moracho, Mascarado, Bayo, Ahumado, Tenado, Azul Curro y Gabino Blanco. No sustituyas Gabino por Gavino ni al revés.
- Mantén observaciones fieles y breves.
- Localiza primero la columna SEXO y revisa su celda en cada fila de pichón de forma independiente. Normalmente contiene una única letra manuscrita: M o H.
- En el JSON copia la marca visible sin interpretarla: si ves M devuelve "M"; si ves H devuelve "H". Nunca intercambies estas letras y no devuelvas las palabras MACHO o HEMBRA.
- Si la celda de sexo está completamente vacía, devuelve null: significa SIN INDICAR y no es un error. Si contiene una marca pero no se distingue claramente entre M y H, devuelve DUDOSO y marca el campo para revisar.
- No deduzcas el sexo por nombre, plumaje, anilla, posición, contexto ni por otras filas.
- Si se proporcionan prefijos de anilla por año, se aplican exclusivamente a los pichones, nunca a padre o madre. Úsalos para resolver letras iniciales ambiguas cuando los números sean legibles. Si la imagen muestra claramente otro prefijo, consérvalo y marca la anilla como dudosa; nunca inventes números.
- Para cada pichón decide por separado el booleano tachado. Usa tachado=true cuando una línea, garabato o trazo atraviesa la fila para anularla, aunque todavía puedan leerse sus datos.
- No interpretes el trazo de tachado como una palabra, especialmente no como "VAREA". Solo transcribe VAREA si se distinguen claramente esas letras escritas como observación independiente.
- No elimines filas tachadas. Si tachado=true, el sistema añadirá MUERTO a observaciones conservando cualquier otra observación legible.
- Usa rutas como padre.nombre, madre.anilla, anio, hoja o pichones.0.anilla en campos_dudosos.
- No incluyas explicaciones fuera del JSON.
`.trim()

function nullableText(value: unknown) {
  return typeof value === 'string' ? value.trim().toLocaleUpperCase('es-ES') || null : null
}

function normalizeSex(value: unknown) {
  const text = nullableText(value)
  if (!text) return null
  const compact = text.replace(/[.\\s]/g, '')
  if (compact === 'M' || compact === 'MACHO' || compact === 'MASCULINO' || text === '♂') return 'MACHO'
  if (compact === 'H' || compact === 'HEMBRA' || compact === 'FEMENINO' || text === '♀') return 'HEMBRA'
  return compact === 'DUDOSO' ? 'DUDOSO' : null
}

function nullableInteger(value: unknown) {
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

export function normalizeDoubtPath(value: string) {
  return value.trim().replace(/\[(\d+)\]/g, '.$1')
}

export function sanitizeAnalysis(value: unknown): SheetAnalysis {
  if (!value || typeof value !== 'object') throw new Error('La respuesta del análisis no es un objeto válido.')
  const source = value as Record<string, unknown>
  const bird = (candidate: unknown) => {
    const item = candidate && typeof candidate === 'object' ? candidate as Record<string, unknown> : {}
    return {
      nombre: nullableText(item.nombre),
      anilla: nullableText(item.anilla),
      plumaje: nullableText(item.plumaje)
    }
  }
  const invalidSexIndexes: number[] = []
  const pichones = Array.isArray(source.pichones) ? source.pichones.map((candidate, index) => {
    const item = candidate && typeof candidate === 'object' ? candidate as Record<string, unknown> : {}
    const sexo = normalizeSex(item.sexo)
    if (item.sexo !== null && item.sexo !== undefined && nullableText(item.sexo) && !sexo) invalidSexIndexes.push(index)
    const observation = nullableText(item.observaciones)
    const observaciones = item.tachado === true && !/\bMUERT[OA]\b/.test(observation || '')
      ? [observation, 'MUERTO'].filter(Boolean).join('; ')
      : observation
    return {
      anilla: nullableText(item.anilla),
      plumaje: nullableText(item.plumaje),
      sexo,
      observaciones
    }
  }) : []
  const campos = Array.isArray(source.campos_dudosos) ? source.campos_dudosos.flatMap(candidate => {
    const item = candidate && typeof candidate === 'object' ? candidate as Record<string, unknown> : {}
    return typeof item.campo === 'string' && typeof item.motivo === 'string'
      ? [{ campo: normalizeDoubtPath(item.campo), motivo: item.motivo.trim() }]
      : []
  }) : []
  pichones.forEach((item, index) => {
    const path = `pichones.${index}.sexo`
    if ((item.sexo === 'DUDOSO' || invalidSexIndexes.includes(index)) && !campos.some(field => field.campo === path)) campos.push({ campo: path, motivo: 'La marca de sexo no se distingue entre M y H' })
  })
  return {
    anio: nullableInteger(source.anio),
    hoja: nullableInteger(source.hoja),
    padre: bird(source.padre),
    madre: bird(source.madre),
    pichones,
    campos_dudosos: campos
  }
}
